import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';

import { PlainDescriptionSection } from '../../../comments/CommentableDescriptionSection';
import { mapJobProfileTaskPreviews } from '../api/profile-task-transport-adapter';
import type { ProfileTask } from '../model/profile-task';
import { toProfileTaskRichTextHtml } from '../profile-task-rich-text';
import { profileTaskTranslationKeys } from '../profile-task-translations';
import { TaskFieldSection, TaskWorkflowStepRow } from '../tile/TaskFieldPrimitives';

type ProfileTaskTitleItem = {
    /** A marker after the title, such as that the task's content is still generating. */
    readonly badge?: ReactNode;
    /** Rendered below the title row, such as the task's content expanded in place. */
    readonly details?: ReactNode;
    /** Set for a title that expands its details in place rather than opening the task. */
    readonly isExpanded?: boolean;
    readonly isSelected?: boolean;
    readonly key: string;
    readonly onOpen?: () => void;
    readonly title: string;
};

export const ProfileTaskTitlesList: FunctionComponent<{ readonly tasks: readonly ProfileTaskTitleItem[] }> = ({
    tasks,
}) => {
    const translate = useTranslate();

    if (tasks.length === 0) {
        return null;
    }

    return (
        <section aria-label={translate(profileTaskTranslationKeys.title)} data-testid="profile-tasks-titles-preview">
            <div className="mb-xl flex items-center gap-l">
                <h3 className="m-0 text-body-s font-medium leading-normal text-text-primary">
                    {translate(profileTaskTranslationKeys.title)}
                </h3>
            </div>
            <ul className="m-0 flex list-none flex-col p-0">
                {tasks.map((task) => (
                    <li key={task.key} data-selected={task.isSelected === true} data-testid="profile-task-title-row">
                        <div className="-ml-m flex w-[calc(100%+var(--spacing-m))] items-center gap-xs rounded-md px-s py-xxs">
                            <span aria-hidden={true} className="select-none px-s">
                                •
                            </span>
                            {task.onOpen === undefined ? (
                                <span className="min-w-0 flex-1 px-xs py-xs text-body-s leading-normal text-text-primary [overflow-wrap:anywhere]">
                                    {task.title}
                                </span>
                            ) : (
                                <Button
                                    aria-expanded={task.isExpanded}
                                    className={clsx(
                                        'min-w-0 flex-1 !justify-start !px-xs !py-xs !font-normal !leading-normal text-left underline-offset-2 [overflow-wrap:anywhere] hover:!text-text-primary hover:!underline hover:!opacity-100',
                                        task.isSelected === true && '!underline',
                                    )}
                                    variant="link"
                                    onClick={task.onOpen}
                                >
                                    {task.title}
                                </Button>
                            )}
                            {task.badge}
                        </div>
                        {task.details}
                    </li>
                ))}
            </ul>
        </section>
    );
};

export type ProfileTaskTitlePreview = {
    /** Position among the non-blank task elements of the TASKS section; the preview task with the same order belongs to it. */
    readonly order: number;
    readonly title: string;
};

const hasPreviewContent = (task: ProfileTask | undefined): task is ProfileTask =>
    is.nonEmptyString(task?.description) || (task?.workflowSteps.length ?? 0) > 0;

const ProfileTaskPreviewContent: FunctionComponent<{ readonly task: ProfileTask }> = ({ task }) => {
    const translate = useTranslate();
    const steps = task.workflowSteps;

    return (
        <div className="flex flex-col gap-l pb-m pl-xl pt-xs" data-testid="profile-task-preview-content">
            {is.nonEmptyString(task.description) ? (
                <PlainDescriptionSection html={toProfileTaskRichTextHtml(task.description)} />
            ) : null}
            {steps.length > 0 ? (
                <TaskFieldSection title={translate(profileTaskTranslationKeys.workflow)}>
                    <ol className="m-0 flex list-none flex-col gap-s p-0">
                        {steps.map((step, index) => (
                            <li key={step.workflowStepId} className="list-none">
                                <TaskWorkflowStepRow description={step.description} index={index} title={step.title} />
                            </li>
                        ))}
                    </ol>
                </TaskFieldSection>
            ) : null}
        </div>
    );
};

/**
 * Read-only tasks section for surfaces without persisted tasks (the job profile template preview):
 * the structured content's task elements carry generated titles but no taskId to join rich task
 * data by. The preview's tasks are matched by order instead and expand in place; without them only
 * the titles render at the section's template position.
 */
export const ProfileTaskTitlesPreview: FunctionComponent<{
    readonly language?: string;
    readonly tasks?: Parameters<typeof mapJobProfileTaskPreviews>[0];
    readonly titles: readonly ProfileTaskTitlePreview[];
}> = ({ language, tasks, titles }) => {
    const { applicationState } = useApplicationContext();
    const [expandedOrders, setExpandedOrders] = useState<ReadonlySet<number>>(() => new Set());
    const tasksByOrder = useMemo(() => mapJobProfileTaskPreviews(tasks, language), [language, tasks]);

    if (!applicationState.featureFlagService.isFeatureFlagEnabled('structured-profile-tasks')) {
        return null;
    }

    const toggle = (order: number): void => {
        setExpandedOrders((current) => {
            const next = new Set(current);

            if (!next.delete(order)) {
                next.add(order);
            }

            return next;
        });
    };

    return (
        <ProfileTaskTitlesList
            tasks={titles.map(({ order, title }) => {
                const task = tasksByOrder.get(order);
                const key = `${order}-${title}`;

                if (!hasPreviewContent(task)) {
                    return { key, title };
                }

                const isExpanded = expandedOrders.has(order);

                return {
                    details: isExpanded ? <ProfileTaskPreviewContent task={task} /> : undefined,
                    isExpanded,
                    isSelected: isExpanded,
                    key,
                    onOpen: () => toggle(order),
                    title,
                };
            })}
        />
    );
};
